// =============================================================================
// MeuDriver — Hook: useInAppNotifications (Batch R1)
// =============================================================================
// Gerencia notificações in-app da tabela `in_app_notifications`.
// Inclui subscription Realtime para badge em tempo real.
// =============================================================================

import { useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { InAppNotificationRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const inAppNotifKeys = {
  all: (userId: string) => ['in_app_notifications', userId] as const,
}

// ---------------------------------------------------------------------------
// useMyInAppNotifications
// ---------------------------------------------------------------------------

export function useMyInAppNotifications() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const channelRef = useRef<RealtimeChannel | null>(null)
  const instanceId = useRef(Math.random().toString(36).slice(2, 8))

  const query = useQuery<InAppNotificationRow[]>({
    queryKey: inAppNotifKeys.all(user?.id ?? ''),
    enabled: !!user?.id,
    staleTime: 1000 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('in_app_notifications')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      return (data ?? []) as InAppNotificationRow[]
    },
  })

  // Realtime subscription
  useEffect(() => {
    if (!user?.id) return

    const channelName = `ian:${user.id}:${instanceId.current}`
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'in_app_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: inAppNotifKeys.all(user.id) })
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'in_app_notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: inAppNotifKeys.all(user.id) })
        },
      )
      .subscribe()

    channelRef.current = channel
    return () => {
      supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [user?.id, queryClient])

  return query
}

// ---------------------------------------------------------------------------
// useUnreadInAppNotificationCount
// ---------------------------------------------------------------------------

export function useUnreadInAppNotificationCount(): number {
  const { data = [] } = useMyInAppNotifications()
  return data.filter((n) => !n.is_read).length
}

// ---------------------------------------------------------------------------
// useMarkInAppNotificationRead
// ---------------------------------------------------------------------------

export function useMarkInAppNotificationRead() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase.rpc('mark_in_app_notification_read', {
        p_notification_id: notificationId,
      })
      if (error) throw error
    },
    onMutate: async (notificationId) => {
      if (!user?.id) return
      const key = inAppNotifKeys.all(user.id)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<InAppNotificationRow[]>(key)
      queryClient.setQueryData<InAppNotificationRow[]>(key, (old = []) =>
        old.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n)),
      )
      return { previous }
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous && user?.id) {
        queryClient.setQueryData(inAppNotifKeys.all(user.id), ctx.previous)
      }
    },
  })
}

// ---------------------------------------------------------------------------
// useMarkAllInAppNotificationsRead
// ---------------------------------------------------------------------------

export function useMarkAllInAppNotificationsRead() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('mark_all_in_app_notifications_read')
      if (error) throw error
    },
    onMutate: async () => {
      if (!user?.id) return
      const key = inAppNotifKeys.all(user.id)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<InAppNotificationRow[]>(key)
      queryClient.setQueryData<InAppNotificationRow[]>(key, (old = []) =>
        old.map((n) => ({ ...n, is_read: true })),
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous && user?.id) {
        queryClient.setQueryData(inAppNotifKeys.all(user.id), ctx.previous)
      }
    },
    onSettled: () => {
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: inAppNotifKeys.all(user.id) })
      }
    },
  })
}
